/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-empty-function */
import {
  Component,
  Input,
  Output,
  EventEmitter,
  forwardRef,
  OnInit,
  OnChanges,
  SimpleChanges
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { UmssSelectComponent, SelectOption } from '../select/select.component';

/**
 * Generic enum select component
 * Accepts any enum record and creates a select dropdown
 *
 * @example
 * ```html
 * <umss-enum-select
 *   [enumRecord]="MESSAGE_TYPE_ENUM_RECORD"
 *   [placeholder]="'Select message type'"
 *   [formControl]="form.controls.type"
 * />
 * ```
 */
@Component({
  selector: 'umss-enum-select',
  standalone: true,
  imports: [CommonModule, UmssSelectComponent],
  template: `
    <umss-select
      [options]="options"
      [placeholder]="placeholder"
      [label]="showLabel ? label : undefined"
      [helperText]="helperText"
      [errorMessage]="errorMessage"
      [size]="size"
      [disabled]="disabled"
      [required]="required"
      [error]="error"
      [loading]="{ selector: loading }"
      [multiple]="multiple"
      [hideSelectedOptions]="hideSelectedOptions"
      [(value)]="value"
      (selectionChange)="onSelectionChange($event)"
      (onFocus)="onFocus()"
      (onBlur)="onBlur()"
    />
  `,
  styles: `
    :host {
      display: contents;
    }
  `,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => UmssEnumSelectComponent),
      multi: true
    }
  ]
})
export class UmssEnumSelectComponent
  implements ControlValueAccessor, OnInit, OnChanges
{
  /**
   * The enum record to create options from
   * Example: MESSAGE_TYPE_ENUM_RECORD, SHIFT_STATUS_RECORD, etc.
   */
  @Input({ required: true }) enumRecord!: Record<string, string>;

  @Input() showLabel = false;
  @Input() label = 'Select';
  @Input() placeholder = 'Select an option';
  @Input() helperText?: string;
  @Input() errorMessage?: string;
  @Input() size: 'Small' | 'Regular' | 'Large' = 'Regular';
  @Input() disabled = false;
  @Input() required = false;
  @Input() error = false;
  @Input() loading = false;
  @Input() multiple = false;
  @Input() hideSelectedOptions = false;
  @Input() sortAlphabetically = false;

  @Input() set value(val: unknown) {
    this._value = val;
  }
  get value(): unknown {
    return this._value;
  }

  @Output() valueChange = new EventEmitter<unknown>();
  @Output() selectionChange = new EventEmitter<unknown>();

  options: SelectOption[] = [];
  private _value: unknown = null;

  private _onChange = (_value: unknown): void => {};
  private _onTouched = (): void => {};

  ngOnInit(): void {
    this.initializeOptions();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['enumRecord'] && !changes['enumRecord'].firstChange)
      this.initializeOptions();
  }

  private initializeOptions(): void {
    if (!this.enumRecord) {
      console.warn('UmssEnumSelectComponent: enumRecord is required');
      return;
    }

    this.options = Object.entries(this.enumRecord).map(
      ([enumValue, label]) => ({ value: enumValue, label: label })
    );

    if (this.sortAlphabetically)
      this.options.sort((a, b) => a.label.localeCompare(b.label));
  }

  /** ControlValueAccessor implementation */
  writeValue(value: unknown): void {
    this._value = value;
  }

  registerOnChange(fn: (value: unknown) => void): void {
    this._onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this._onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  /** Event handlers */
  onSelectionChange(value: unknown): void {
    this._value = value;
    this._onChange(value);
    this._onTouched();
    this.valueChange.emit(value);
    this.selectionChange.emit(value);
  }

  onFocus(): void {
    // Can be used for additional logic if needed
  }

  onBlur(): void {
    this._onTouched();
  }
}
